/**
 * Routing Utilities
 * Provides helper functions for domain extraction and navigation
 */

import type { History } from "history";

/**
 * Extracts the user identifier (domain) from the current URL
 * Handles three domain patterns:
 * 1. site.fotoowl.ai/photographer-name -> returns "site.fotoowl.ai/photographer-name"
 * 2. photographername.fotoowl.ai -> returns "photographername.fotoowl.ai"
 * 3. photographer.com -> returns "photographer.com"
 * 4. localhost/photographer-name (with path-based pattern) -> returns "site.fotoowl.ai/photographer-name" (or "dev-site.fotoowl.ai/photographer-name" in dev)
 *
 * @returns {string} The user identifier (domain)
 */
export const getUserIdentifier = (): string => {
    if (typeof window === "undefined") return "";

    const hostname = window.location.hostname;
    const pathname = window.location.pathname;
    const searchParams = new URLSearchParams(window.location.search);
    const proxyModeParam = searchParams.get("proxy_mode");
    const isProxyMode = proxyModeParam === "true" || proxyModeParam === "1";

    // Check if we're using path-based pattern (site.fotoowl.ai or localhost with path-based)
    if (isSiteFotoowlPattern()) {
        // Extract photographer-name from path: site.fotoowl.ai/photographer-name or localhost/photographer-name
        // Path format: /photographer-name/... or /photographer-name
        const pathParts = pathname.split("/").filter((part) => part);
        if (pathParts.length > 0) {
            // For localhost (or proxy mode), use site.fotoowl.ai (or dev-site.fotoowl.ai in dev) instead of the current hostname
            let baseHostname = hostname;
            if (isProxyMode || hostname.includes("localhost") || hostname === "127.0.0.1") {
                const ENV = import.meta.env?.VITE_ENV;
                baseHostname = ENV === "dev" ? "dev-site.fotoowl.ai" : ENV === "staging" ? "staging-site.fotoowl.ai" : "site.fotoowl.ai";
            }
            // Return full identifier: site.fotoowl.ai/photographer-name (or dev-site.fotoowl.ai/photographer-name)
            return `${baseHostname}/${pathParts[0]}`;
        }
        // Fallback to hostname if no path segment (but still convert localhost)
        if (isProxyMode || hostname.includes("localhost") || hostname === "127.0.0.1") {
            const ENV = import.meta.env?.VITE_ENV;
            return ENV === "dev" ? "dev-site.fotoowl.ai" : ENV === "staging" ? "staging-site.fotoowl.ai" : "site.fotoowl.ai";
        }
        return hostname;
    }

    // Otherwise, it's a custom domain (e.g., photographername.fotoowl.ai or photographer.com)
    return hostname;
};

/**
 * Extracts the domain pattern from the current URL path
 * Used for routing with :domainPattern parameter
 * Returns the first path segment if on site.fotoowl.ai or localhost (with path-based pattern), empty string otherwise
 *
 * @returns {string} The domain pattern (e.g., "photographer-name" or "")
 */
export const getDomainPattern = (): string => {
    if (typeof window === "undefined") return "";

    const pathname = window.location.pathname;

    // Check if we're using path-based pattern (site.fotoowl.ai or localhost with path-based)
    if (isSiteFotoowlPattern()) {
        // Extract photographer-name from path: site.fotoowl.ai/photographer-name or localhost/photographer-name
        const pathParts = pathname.split("/").filter((part) => part);
        if (pathParts.length > 0) {
            return pathParts[0];
        }
    }

    // For subdomain or custom domain, no domain pattern in path
    return "";
};

/**
 * Checks if the current domain uses the site.fotoowl.ai pattern
 * (i.e., photographer-name is in the path rather than subdomain)
 * 
 * On localhost, can be forced via query parameter: ?site_pattern=true
 * or automatically detected if first path segment exists and looks like a tenant
 *
 * @returns {boolean} True if using site.fotoowl.ai pattern
 */
export const isSiteFotoowlPattern = (): boolean => {
    if (typeof window === "undefined") return false;

    const hostname = window.location.hostname;
    const searchParams = new URLSearchParams(window.location.search);

    // Check for explicit query parameter override (useful for localhost testing)
    const sitePatternParam = searchParams.get("site_pattern");
    if (sitePatternParam === "true" || sitePatternParam === "1") {
        return true;
    }
    if (sitePatternParam === "false" || sitePatternParam === "0") {
        return false;
    }

    // Production check: site.fotoowl.ai
    if (hostname.includes("site.fotoowl.ai") || hostname.includes("dev-site.fotoowl.ai") || hostname.includes("staging-site.fotoowl.ai")) {
        return true;
    }

    // Localhost: auto-detect path-based pattern if first path segment exists
    // This allows testing path-based routing on localhost without query params
    if (hostname.includes("localhost") || hostname === "127.0.0.1") {
        const pathname = window.location.pathname;
        const pathParts = pathname.split("/").filter((part) => part);
        // If there's a first path segment, assume path-based pattern
        // (This will be validated by reserved route check in App.jsx)
        return pathParts.length > 0;
    }

    return false;
};

/**
 * Navigates to the gallery page for a given event ID
 * Handles domain patterns correctly:
 * - site.fotoowl.ai/photographer-name -> /photographer-name/gallery/:eventId
 * - photographername.fotoowl.ai -> /gallery/:eventId
 * - photographer.com -> /gallery/:eventId
 *
 * @param {string|number} eventId - The event ID
 * @param {object} options - Optional navigation options
 * @param {object} options.history - React Router history object (if using class components)
 * @param {Function} options.navigate - React Router navigate function (if using hooks)
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 */
export const gotoGallery = (
    eventId: string | number,
    options?: {
        history?: History;
        navigate?: (path: string, options?: { replace?: boolean }) => void;
        replace?: boolean;
    }
): void => {
    if (typeof window === "undefined") return;

    const domainPattern = getDomainPattern();
    const queryParams = window.location.search;
    const galleryPath = domainPattern
        ? `/${domainPattern}/gallery/${eventId}`
        : `/gallery/${eventId}`;
    const galleryPathWithQuery = `${galleryPath}${queryParams}`;

    // Use navigate function if provided (React Router v6)
    if (options?.navigate) {
        options.navigate(galleryPathWithQuery, { replace: options.replace ?? false });
        return;
    }

    // Use history object if provided (React Router v5)
    if (options?.history) {
        if (options.replace) {
            options.history.replace(galleryPathWithQuery);
        } else {
            options.history.push(galleryPathWithQuery);
        }
        return;
    }

    // Fallback to window.location
    window.location.href = `${window.location.origin}${galleryPath}${queryParams}`;
};

/**
 * Navigates to the transaction page for a given event ID
 * Handles domain patterns correctly:
 * - site.fotoowl.ai/photographer-name -> /photographer-name/transaction/:eventId
 * - photographername.fotoowl.ai -> /transaction/:eventId
 * - photographer.com -> /transaction/:eventId
 *
 * @param {string|number} eventId - The event ID
 * @param {object} options - Optional navigation options
 * @param {object} options.history - React Router history object (if using class components)
 * @param {Function} options.navigate - React Router navigate function (if using hooks)
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 */
export const gotoTransaction = (
    eventId: string | number,
    options?: {
        history?: History;
        navigate?: (path: string, options?: { replace?: boolean }) => void;
        replace?: boolean;
    }
): void => {
    if (typeof window === "undefined") return;

    const domainPattern = getDomainPattern();
    const queryParams = window.location.search;
    const transactionPath = domainPattern
        ? `/${domainPattern}/transaction/${eventId}`
        : `/transaction/${eventId}`;
    const transactionPathWithQuery = `${transactionPath}${queryParams}`;

    // Use navigate function if provided (React Router v6)
    if (options?.navigate) {
        options.navigate(transactionPathWithQuery, { replace: options.replace ?? false });
        return;
    }

    // Use history object if provided (React Router v5)
    if (options?.history) {
        if (options.replace) {
            options.history.replace(transactionPathWithQuery);
        } else {
            options.history.push(transactionPathWithQuery);
        }
        return;
    }

    // Fallback to window.location
    window.location.href = `${window.location.origin}${transactionPath}${queryParams}`;
};

/**
 * Navigates to the reelit page for a given event ID
 * Handles domain patterns correctly:
 * - site.fotoowl.ai/photographer-name -> /photographer-name/reelit/:eventId
 * - photographername.fotoowl.ai -> /reelit/:eventId
 * - photographer.com -> /reelit/:eventId
 *
 * @param {string|number} eventId - The event ID
 * @param {object} options - Optional navigation options
 * @param {object} options.history - React Router history object (if using class components)
 * @param {Function} options.navigate - React Router navigate function (if using hooks)
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 */
export const gotoReelit = (
    eventId: string | number,
    options?: {
        history?: History;
        navigate?: (path: string, options?: { replace?: boolean }) => void;
        replace?: boolean;
    }
): void => {
    if (typeof window === "undefined") return;

    const domainPattern = getDomainPattern();
    const queryParams = window.location.search;
    const reelitPath = domainPattern
        ? `/${domainPattern}/gallery/${eventId}/reelit`
        : `/gallery/${eventId}/reelit`;
    const reelitPathWithQuery = `${reelitPath}${queryParams}`;

    // Use navigate function if provided (React Router v6)
    if (options?.navigate) {
        options.navigate(reelitPathWithQuery, { replace: options.replace ?? false });
        return;
    }

    // Use history object if provided (React Router v5)
    if (options?.history) {
        if (options.replace) {
            options.history.replace(reelitPathWithQuery);
        } else {
            options.history.push(reelitPathWithQuery);
        }
        return;
    }

    // Fallback to window.location
    window.location.href = `${window.location.origin}${reelitPath}${queryParams}`;
};

/**
 * Navigates to the "My Photos" page using a redirect URL from face search response
 * Handles domain patterns correctly:
 * - site.fotoowl.ai/photographer-name -> /photographer-name/{redirectPath}
 * - photographername.fotoowl.ai -> /{redirectPath}
 * - photographer.com -> /{redirectPath}
 *
 * Preserves query parameters from redirect URL and current URL (album_selection, vip-link)
 * Adds request_id and request_key from response to the navigation URL
 *
 * @param {string} redirectUrl - The redirect URL from the response
 * @param {object} options - Optional navigation options
 * @param {string} options.requestId - Request ID to add to query params
 * @param {string} options.requestKey - Request key to add to query params
 * @param {object} options.history - React Router history object (if using class components)
 * @param {Function} options.navigate - React Router navigate function (if using hooks)
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 */
export const gotoMyPhotos = (
    redirectUrl: string,
    options?: {
        requestId?: string;
        requestKey?: string;
        shareKey?: string;
        accessKey?: string;
        history?: History;
        navigate?: (path: string, options?: { replace?: boolean }) => void;
        replace?: boolean;
    }
): void => {
    if (typeof window === "undefined") return;

    // Extract path and query params from redirect_url
    // Handle both full URLs and relative paths
    let redirectPath: string;
    let redirectQueryParams: URLSearchParams;

    try {
        // Try to parse as full URL first
        const redirectUrlObj = new URL(redirectUrl);
        redirectPath = redirectUrlObj.pathname;
        redirectQueryParams = new URLSearchParams(redirectUrlObj.search);
    } catch {
        // If it fails, treat as relative path
        const [path, query] = redirectUrl.split("?");
        redirectPath = path;
        redirectQueryParams = new URLSearchParams(query || "");
    }

    // Add request_id, request_key, and shareKey from response to redirect URL
    if (options?.requestId) {
        redirectQueryParams.set("request_id", options.requestId);
    }
    if (options?.requestKey) {
        redirectQueryParams.set("request_key", options.requestKey);
    }
    if (options?.shareKey) {
        redirectQueryParams.set("share_key", options.shareKey);
    }
    if (options?.accessKey) {
        redirectQueryParams.set("access_key", options.accessKey);
    }

    // Extract query params from current URL
    const currentQueryParams = new URLSearchParams(window.location.search);
    // Preserve album_selection and vip-link from current URL
    if (currentQueryParams.has("album_selection")) {
        redirectQueryParams.set(
            "album_selection",
            currentQueryParams.get("album_selection") || ""
        );
    }
    if (currentQueryParams.has("vip-link")) {
        redirectQueryParams.set("vip-link", currentQueryParams.get("vip-link") || "");
    }

    // Handle domain pattern
    const domainPattern = getDomainPattern();
    if (domainPattern) {
        // Check if redirect path already has domain pattern
        const pathParts = redirectPath.split("/").filter((part) => part);
        if (pathParts.length > 0 && pathParts[0] !== domainPattern) {
            // Add domain pattern prefix if not already present
            redirectPath = `/${domainPattern}${redirectPath}`;
        }
    }

    const finalPathWithQuery = `${redirectPath}?${redirectQueryParams.toString()}`;

    // Use navigate function if provided (React Router v6)
    if (options?.navigate) {
        options.navigate(finalPathWithQuery, { replace: options.replace ?? false });
        return;
    }

    // Use history object if provided (React Router v5)
    if (options?.history) {
        if (options.replace) {
            options.history.replace(finalPathWithQuery);
        } else {
            options.history.push(finalPathWithQuery);
        }
        return;
    }

    // Fallback to window.location
    window.location.href = `${window.location.origin}${finalPathWithQuery}`;
};

/**
 * Navigates to the register page for a given event ID
 * Handles domain patterns correctly:
 * - site.fotoowl.ai/photographer-name -> /photographer-name/gallery/:eventId/register or /photographer-name/gallery/:accessKey/:eventId/register
 * - photographername.fotoowl.ai -> /gallery/:eventId/register or /gallery/:accessKey/:eventId/register
 * - photographer.com -> /gallery/:eventId/register or /gallery/:accessKey/:eventId/register
 *
 * @param {string|number} eventId - The event ID
 * @param {object} options - Optional navigation options
 * @param {string} options.accessKey - Optional access key for the gallery
 * @param {object} options.history - React Router history object (if using class components)
 * @param {Function} options.navigate - React Router navigate function (if using hooks)
 * @param {boolean} options.replace - Whether to replace current history entry (default: false)
 */
export const gotoRegister = (
    eventId: string | number,
    options?: {
        accessKey?: string;
        history?: History;
        navigate?: (path: string, options?: { replace?: boolean }) => void;
        replace?: boolean;
    }
): void => {
    if (typeof window === "undefined") return;

    const domainPattern = getDomainPattern();
    const queryParams = window.location.search;

    let registerPath: string;
    if (options?.accessKey) {
        // Has access key: /:domainPattern/gallery/:accessKey/:eventId/register or /gallery/:accessKey/:eventId/register
        registerPath = domainPattern
            ? `/${domainPattern}/gallery/${options.accessKey}/${eventId}/register`
            : `/gallery/${options.accessKey}/${eventId}/register`;
    } else {
        // No access key: /:domainPattern/gallery/:eventId/register or /gallery/:eventId/register
        registerPath = domainPattern
            ? `/${domainPattern}/gallery/${eventId}/register`
            : `/gallery/${eventId}/register`;
    }

    const registerPathWithQuery = `${registerPath}${queryParams}`;

    // Use navigate function if provided (React Router v6)
    if (options?.navigate) {
        options.navigate(registerPathWithQuery, { replace: options.replace ?? false });
        return;
    }

    // Use history object if provided (React Router v5)
    if (options?.history) {
        if (options.replace) {
            options.history.replace(registerPathWithQuery);
        } else {
            options.history.push(registerPathWithQuery);
        }
        return;
    }

    // Fallback to window.location
    window.location.href = `${window.location.origin}${registerPath}${queryParams}`;
};

/**
 * Builds a path for a given route, handling domain patterns correctly
 *
 * @param {string} route - The route path (e.g., "/gallery/:eventId")
 * @param {Record<string, string | number>} params - Route parameters to replace
 * @returns {string} The full path with domain pattern if needed
 *
 * @example
 * buildPath("/gallery/:eventId", { eventId: 123 })
 * // Returns: "/photographer-name/gallery/123" or "/gallery/123" depending on domain
 */
export const buildPath = (
    route: string,
    params: Record<string, string | number> = {}
): string => {
    const domainPattern = getDomainPattern();
    let path = route;

    // Replace route parameters
    Object.entries(params).forEach(([key, value]) => {
        path = path.replace(`:${key}`, String(value));
    });

    // Add domain pattern prefix if needed
    if (domainPattern && !path.startsWith(`/${domainPattern}`)) {
        path = `/${domainPattern}${path}`;
    }

    return path;
};

